<template>
  <Teleport to="body">
    <Transition name="ui-modal">
      <div
        v-if="modelValue"
        v-dialog-focus="{ onEscape: close }"
        class="ui-modal"
        role="dialog"
        aria-modal="true"
        v-bind="title ? { 'aria-labelledby': titleId } : {}"
        @click.self="persistent ? undefined : close()"
      >
        <div class="ui-modal__box" :style="{ maxWidth: `${effectiveMaxWidth}px` }">
          <header v-if="title || slots['actions']" class="ui-modal__header">
            <h2 v-if="title" :id="titleId" class="ui-modal__title">{{ title }}</h2>
            <div class="ui-modal__actions">
              <slot name="actions" />
            </div>
          </header>
          <div class="ui-modal__body">
            <slot />
          </div>
          <div v-if="slots['footer']" class="ui-modal__footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useId, useSlots } from 'vue'
import { vDialogFocus } from '@/shared/lib/dialogFocus'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title?: string
    /** Не закрывать по клику по оверлею */
    persistent?: boolean
    maxWidth?: number
  }>(),
  { persistent: false },
)

const titleId = `ui-modal-title-${useId()}`
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
const effectiveMaxWidth = computed(() => {
  const m = props.maxWidth ?? 500
  return Math.min(m, windowWidth.value - 32)
})

function updateWidth() {
  windowWidth.value = typeof window !== 'undefined' ? window.innerWidth : 1024
}
onMounted(() => {
  window.addEventListener('resize', updateWidth)
  updateWidth()
})
onUnmounted(() => {
  window.removeEventListener('resize', updateWidth)
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'close'): void
}>()
const slots = useSlots()

function close() {
  if (props.persistent) return
  emit('update:modelValue', false)
  emit('close')
}
</script>

<style scoped>
.ui-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.72);
  box-sizing: border-box;
}

.ui-modal__box {
  width: 100%;
  max-width: 500px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ui-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.ui-modal__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.ui-modal__actions {
  display: flex;
  gap: 8px;
}

.ui-modal__body {
  padding: 16px;
  overflow-y: auto;
  flex: 1;
}

.ui-modal__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #e2e8f0;
}

.ui-modal-enter-active,
.ui-modal-leave-active {
  transition: opacity 0.2s ease;
}
.ui-modal-enter-from,
.ui-modal-leave-to {
  opacity: 0;
}
.ui-modal-enter-active .ui-modal__box,
.ui-modal-leave-active .ui-modal__box {
  transition: transform 0.2s ease;
}
.ui-modal-enter-from .ui-modal__box,
.ui-modal-leave-to .ui-modal__box {
  transform: scale(0.98);
}
</style>
